import { createApi } from "@reduxjs/toolkit/query/react";

import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { REQUEST_METHODS } from "@/constants/global-constants";
import { IDrive } from "@/interfaces/project-register.interface";
import {
  ExtendedDriveResponse,
  IProject,
} from "@/interfaces/project.interface";
import { TSpecialRate } from "@/interfaces/special-rate.interface";
import {
  IAssignTool,
  IProductAssignment,
  IProductLogsItem,
  IProjectAssignedTool,
} from "@/interfaces/tools.interface";
import { BaseWithAuth, getToken, getWorkspace } from "@/utils/helpers";

export const projectApi = createApi({
  reducerPath: "projectApi",
  baseQuery: BaseWithAuth(),
  tagTypes: [
    "projects",
    "drive",
    "specialRate",
    "singleProject",
    "assignTools",
    "single-assignTools",
  ],
  endpoints: (builder) => ({
    // get projects by status
    getProjectsByStatus: builder.query<
      GeneralResponse<IProject[]>,
      { status: string; filter?: string }
    >({
      query: ({ status, filter }) =>
        `workspace/${getWorkspace()}/project${status ? `?status=${status}` : ""}${filter ? filter : ""}`,
      providesTags: ["projects"],
      transformResponse: (response: GeneralResponse<IProject[]>) => response,
    }),
    // get single project
    getSingleProject: builder.query<
      IProject | IDrive[],
      { projectId: string; params?: string }
    >({
      query: ({ projectId, params }) =>
        `workspace/${getWorkspace()}/project/${projectId}/${params ? params : ""}`,

      transformResponse: (response: APIResponse<IProject>) => response?.data,
      providesTags: ["singleProject"],
    }),

    // get customer project
    getCustomerProject: builder.query<
      GeneralResponse<IProject[]>,
      { customerId: string; projectStatus?: string; filter?: string }
    >({
      query: ({ customerId, projectStatus, filter }) => {
        if (projectStatus) {
          return `workspace/${getWorkspace()}/project?customerId=${customerId}&status=${projectStatus}${filter ? filter : ""}`;
        }

        return `workspace/${getWorkspace()}/project?customerId=${customerId}${filter ? filter : ""}`;
      },
      providesTags: ["projects"],
      // transformResponse: (response: APIResponse<IProject[]>) => response?.data,
    }),

    // add project
    addProject: builder.mutation<IProject, { data: object }>({
      query: ({ data }) => ({
        url: `workspace/${getWorkspace()}/project`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["projects"],
    }),

    // mutate project
    alterProject: builder.mutation<
      IProject,
      {
        projectId?: string;
        data?: object;
        method: REQUEST_METHODS | "GET";
        params?: string;
        timeout?: number;
      }
    >({
      query: ({ projectId, data, method, params, timeout }) => ({
        url: `workspace/${getWorkspace()}/project/${projectId}/${params ? params : ""}`,
        method,
        body: data,
        timeout,
      }),
      invalidatesTags: ["projects", "singleProject"],
    }),

    // delete project induction
    deleteProjectInduction: builder.mutation<
      unknown,
      { induction_id: string; project_id: string }
    >({
      query: ({ induction_id, project_id }) => ({
        url: `workspace/${getWorkspace()}/project/${project_id}/induction/${induction_id}`,
        method: "DELETE",
      }),
      // invalidatesTags: ["projects"],
    }),
    specialRateForProject: builder.mutation<
      object,
      { id: string; data: object }
    >({
      query: ({ data, id }) => ({
        url: `workspace/${getWorkspace()}/project/${id}/rate`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["specialRate"],
    }),
    getProjectSpecialRate: builder.query<TSpecialRate, { id: string }>({
      query: ({ id }) => `workspace/${getWorkspace()}/project/${id}/rate`,
      providesTags: ["specialRate"],
      transformResponse: (response: APIResponse<TSpecialRate>) =>
        response?.data,
    }),

    getProjectDrive: builder.query<
      ExtendedDriveResponse,
      { projectId: string; params?: string }
    >({
      query: ({ projectId, params }) => ({
        url: `workspace/${getWorkspace()}/project/${projectId}/${params ? params : `driveType/${driveTypes.PROJECT}`}`,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }),
      providesTags: ["drive", "projects"],
    }),

    //Tools Queries
    //get all Project Tools
    getToolsAssignToProject: builder.query<
      GeneralResponse<IProjectAssignedTool[] | IAssignTool[]> & {
        logs: IProductLogsItem[];
      },
      { projectId: string; params?: string; id?: string }
    >({
      query: ({ projectId, params, id }) => ({
        url: `workspace/${getWorkspace()}/project/${projectId}/tool${params ? params : ""}${id ? `/${id}` : ""}`,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: "assignTools", id: projectId },
      ],
    }),

    //Assign Tools to Project
    assignToolsToProject: builder.mutation<
      object,
      { projectId: string; body: object }
    >({
      query: ({ projectId, body }) => ({
        url: `workspace/${getWorkspace()}/project/${projectId}/tool`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: "assignTools", id: projectId },
      ],
    }),

    //Comment
    addCommentsInTools: builder.mutation<
      object,
      { projectId: string; data: object; id: string }
    >({
      query: ({ projectId, data, id }) => ({
        url: `workspace/${getWorkspace()}/project/${projectId}/tool/${id}/comment`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "single-assignTools", id },
      ],
    }),

    //get single Project Tools
    getSingleProductAssignment: builder.query<
      IProductAssignment,
      { projectId: string; productAssignmentId: string }
    >({
      query: ({ productAssignmentId, projectId }) => ({
        url: `workspace/${getWorkspace()}/project/${projectId}/tool/productAssignment/${productAssignmentId}`,
      }),
      transformResponse: (response: APIResponse<IProductAssignment>) =>
        response?.data,
      providesTags: (_result, _error, { productAssignmentId }) => [
        { type: "single-assignTools", id: productAssignmentId },
      ],
    }),

    //Fetch all Project Tools assignment
    getAllProductAssignment: builder.query<
      GeneralResponse<IProductAssignment[]>,
      { projectId: string; filters?: string; status?: string }
    >({
      query: ({ projectId, filters, status }) => ({
        url: ((): string => {
          const baseUrl = `workspace/${getWorkspace()}/project/${projectId}/tool/productAssignment`;
          if (status && filters) {
            return `${baseUrl}?status=${status}&${filters}`;
          } else if (status) {
            return `${baseUrl}?status=${status}`;
          } else if (filters) {
            return `${baseUrl}?${filters}`;
          }

          return baseUrl;
        })(),
      }),
      providesTags: ["assignTools"],
    }),

    // Delete assigned tools
    deleteAssignedTools: builder.mutation<
      object,
      { projectId: string; id: string }
    >({
      query: ({ projectId, id }) => ({
        url: `workspace/${getWorkspace()}/project/${projectId}/tool/productAssignment/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["assignTools"],
    }),

    //Update assigned tools details
    updateAssignedTools: builder.mutation<
      object,
      { projectId: string; id: string; body: object }
    >({
      query: ({ projectId, id, body }) => ({
        url: `workspace/${getWorkspace()}/project/${projectId}/tool/productAssignment/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["single-assignTools", "assignTools"],
    }),

    // Assigned --> Awaiting Tools
    sendToSiteAssignedTools: builder.mutation<
      object,
      { projectId: string; body: object; productAssignmentBatchId: string }
    >({
      query: ({ projectId, body, productAssignmentBatchId }) => ({
        url: `workspace/${getWorkspace()}/project/${projectId}/tool/${productAssignmentBatchId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["assignTools"],
    }),
  }),
});

export const {
  useGetAllProductAssignmentQuery,
  useGetSingleProductAssignmentQuery,
  useGetProjectsByStatusQuery,
  useGetCustomerProjectQuery,
  useGetSingleProjectQuery,
  useAddProjectMutation,
  useAlterProjectMutation,
  useDeleteProjectInductionMutation,
  // useAlterProjectInductionMutation,
  useSpecialRateForProjectMutation,
  useGetProjectSpecialRateQuery,
  useGetProjectDriveQuery,
  useAssignToolsToProjectMutation,
  useAddCommentsInToolsMutation,
  useGetToolsAssignToProjectQuery,
  useUpdateAssignedToolsMutation,
  useDeleteAssignedToolsMutation,
  useSendToSiteAssignedToolsMutation,
} = projectApi;
